import React, { FC, useCallback, useState } from 'react';
import { MuiPickersUtilsProvider } from '@material-ui/pickers';
import { DatePicker } from 'mui-rff';
import * as yup from 'yup';
import DateFnsUtils from '@date-io/date-fns';
import enLocale from 'date-fns/locale/en-GB';
import { ThemeProvider, useTheme } from '@material-ui/core/styles';
import { createTheme, useMediaQuery } from '@material-ui/core';
import Form from 'ui/Form/Form';
import Button, { Themes as ButtonThemes } from 'ui/Button/Button';
import { validationCallback } from 'utils/validation';
import ModalWindow from '../ModalWindow/ModalWindow';
import { IDatePickerRangeModalProps } from '../IModalProps';

// eslint-disable-next-line css-modules/no-unused-class
import styles from './DatePickerRangeModal.module.css';

import calendar from 'assets/img/calendar.png';

const schema = yup.object().shape({
  startDate: yup
    .date()
    .label('Start Date')
    .when('endDate', (endDate, sch) => sch.max(endDate, 'Start date should not be greater than end date')),
  endDate: yup.date(),
});

const DatePickerRangeModal: FC<IDatePickerRangeModalProps> = ({
  open,
  startRangeDate,
  endRangeDate,
  getDateRange,
  onClose,
}) => {
  const [startDate, setStartDate] = React.useState<Date | null | undefined | number>(
    new Date(new Date().setDate(new Date().getDate() - 30)),
  );
  const [endDate, setEndDate] = React.useState<Date | null | undefined | number>(
    new Date(new Date().setDate(new Date().getDate() + 60)),
  );

  const [activePickerName, setActivePickerName] = useState<'start' | 'end' | null>(null);

  const setStartPickerActive = () => setActivePickerName('start');
  const setEndPickerActive = () => setActivePickerName('end');
  const setNoPickerActive = () => setActivePickerName(null);

  const validate = useCallback(validationCallback<unknown>(schema), []);

  const onCancel = () => {
    setStartDate(startRangeDate);
    setEndDate(endRangeDate);
    onClose();
  };

  const onApply = () => {
    if (getDateRange) {
      getDateRange(startDate, endDate);
      onClose();
    }
  };

  const theme = useTheme();
  const smallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const materialTheme = createTheme({
    overrides: {
      MuiFilledInput: {
        root: {
          backgroundColor: '#F6F7F9',
          borderRadius: '4px',
          '&:hover': {
            backgroundColor: '#F6F7F9',
          },
          '&.Mui-focused': {
            backgroundColor: '#F6F7F9',
          },
          '&.Mui-error': {
            border: '1px solid #f44336',
          },
        },
        input: {
          height: '23px',
          fontSize: '14px',
          fontFamily: 'var(--ff-etelka-text)',
          color: '#222736',
          cursor: 'pointer',
        },
      },
      MuiInputLabel: {
        root: {
          height: '16px',
          fontSize: '12px',
          fontFamily: 'var(--ff-etelka-text)',
          color: '#7D8598',
          '&.Mui-focused': {
            color: '#7D8598',
          },
          '&.Mui-error.Mui-focused': {
            color: '#f44336',
          },
        },
        formControl: {
          top: '5px',
        },
      },
      MuiPaper: {
        root: {
          width: '368px',
          height: '402px',
          marginTop: smallScreen ? 0 : '8px',
          marginLeft: smallScreen ? 0 : '15px',
          border: '1px solid #E5E5E5',
          boxSizing: 'border-box',
          borderRadius: '4px',
        },
      },
      MuiPickersBasePicker: {
        pickerView: {
          maxWidth: '100%',
          maxHeight: '100%',
          overflowX: 'unset',
        },
      },
      MuiPickersCalendarHeader: {
        switchHeader: {
          height: '24px',
          width: '336px',
          margin: smallScreen ? '0 3px' : '0 16px',
          marginTop: '24px',
          marginBottom: '24px',
        },
        daysHeader: {
          maxHeight: '24px',
          margin: smallScreen ? '0 3px' : '0 16px',
        },
        dayLabel: {
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '48px',
          height: '24px',
          margin: 0,
          fontSize: '12px',
          fontFamily: 'var(--ff-etelka-text)',
        },
        iconButton: {
          padding: 0,
        },
      },
      MuiTypography: {
        body1: {
          fontSize: '14px',
          lineHeight: '24px',
          fontFamily: 'var(--ff-etelka-text)',
          color: '#222736',
        },
        body2: {
          fontSize: '14px',
          fontFamily: 'var(--ff-etelka-text)',
          color: '#222736',
        },
      },
      MuiPickersDay: {
        daySelected: {
          backgroundColor: '#4F2651',
          '&:hover': {
            backgroundColor: '#4F2651',
          },
        },
        day: {
          width: '48px',
          height: '48px',
          margin: 0,
        },
        current: {
          color: '#4F2651',
        },
      },
      MuiPickersCalendar: {
        week: {
          height: '48px',
          margin: '0 16px',
        },
      },
    },
  });

  return (
    <ModalWindow title="Date Period" open={open} onClose={onClose}>
      <Form
        onSubmit={onApply}
        validate={validate}
        initialValues={{
          startDate,
          endDate,
        }}
        render={({ handleSubmit, submitting, valid }) => (
          <form onSubmit={handleSubmit}>
            <MuiPickersUtilsProvider utils={DateFnsUtils} locale={enLocale}>
              <div className={styles.pickersWrapper}>
                <ThemeProvider theme={materialTheme}>
                  <DatePicker
                    name="startDate"
                    className={activePickerName === 'start' && valid ? styles.dateActive : ''}
                    format="dd-MM-yyyy"
                    inputVariant="filled"
                    label="Start Date"
                    size="small"
                    variant="inline"
                    fieldProps={{
                      onChange: setStartDate,
                    }}
                    InputProps={{
                      endAdornment: <img src={calendar} alt="Calendar logo" />,
                      disableUnderline: true,
                    }}
                    onOpen={setStartPickerActive}
                    onClose={setNoPickerActive}
                    autoOk
                    disableFuture
                    disableToolbar
                    fullWidth
                  />
                </ThemeProvider>
                <ThemeProvider theme={materialTheme}>
                  <DatePicker
                    name="endDate"
                    className={activePickerName === 'end' ? styles.dateActive : ''}
                    format="dd-MM-yyyy"
                    inputVariant="filled"
                    label="End Date"
                    size="small"
                    variant="inline"
                    InputProps={{
                      endAdornment: <img src={calendar} alt="Calendar logo" />,
                      disableUnderline: true,
                    }}
                    fieldProps={{
                      onChange: setEndDate,
                    }}
                    onOpen={setEndPickerActive}
                    onClose={setNoPickerActive}
                    autoOk
                    disableToolbar
                    fullWidth
                  />
                </ThemeProvider>
              </div>
            </MuiPickersUtilsProvider>
            <div className={styles.buttonsWrapper}>
              <Button theme={ButtonThemes.secondary} className={styles.button} onClick={onCancel}>
                Cancel
              </Button>
              <Button
                theme={ButtonThemes.primary}
                className={styles.button}
                disabled={submitting || !valid}
                type="submit"
              >
                Apply
              </Button>
            </div>
          </form>
        )}
      />
    </ModalWindow>
  );
};

export default DatePickerRangeModal;
